import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button,
  TextField,
  MenuItem,
  Typography,
  Chip,
  Divider,
  Tabs,
  Tab,
  Box,
  Stepper,
  Step,
  StepLabel,
} from "@material-ui/core";

import select from "../../utils/select";
import {
  getTopicById,
  submitTopic,
  approveTopic,
  returnTopicToDraft,
  resumeTopic,
  closeTopic,
  requestAcceptance,
  setAcceptance,
  addProgressReport,
  requestExtension,
  approveExtension,
  requestStop,
  approveStop,
  sendTopicEmail,
} from "../../reducer/researchTopic/action";
import {
  TOPIC_STEPS,
  topicStepIndex,
  topicVisibleTabs,
  requestStatusLabel,
  statusLabel,
  statusColor,
  levelLabel,
  roleLabel,
} from "../../constants/topicStatus";
import MultiFileManager from "../File/MultiFileManager/MultiFileManager";

const TAB_LABELS = {
  info: "Thông tin",
  members: "Thành viên",
  progress: "Tiến độ",
  extension: "Gia hạn",
  stop: "Xin dừng",
  acceptance: "Nghiệm thu",
  email: "Gửi email",
};

const Row = ({ label, children }) => (
  <Grid container spacing={1} style={{ marginBottom: 4 }}>
    <Grid item xs={4}><Typography variant="body2" color="textSecondary">{label}</Typography></Grid>
    <Grid item xs={8}><Typography variant="body2">{children}</Typography></Grid>
  </Grid>
);

// Chip nhỏ hiển thị trạng thái yêu cầu con (gia hạn / xin dừng) bằng tiếng Việt.
const ReqStatusChip = ({ status }) => {
  const color = status === "approved" ? "#2e7d32" : status === "rejected" ? "#d32f2f" : "#f9a825";
  return <Chip size="small" label={requestStatusLabel(status)} style={{ backgroundColor: color, color: "#fff" }} />;
};

export default function ResearchTopicDetailModal({ open, onClose, topicId, reloadPayload }) {
  const dispatch = useDispatch();
  const role = useSelector((s) => select(s, "userReducer", "role"));
  const isAdmin = role === "sudo-admin";
  const detail = useSelector((s) => {
    const v = select(s, "researchTopicReducer", "detail");
    return v && v.toJS ? v.toJS() : v;
  });
  const [tab, setTab] = useState("info");
  const [report, setReport] = useState({ finishedWork: "", content: "" });
  const [ext, setExt] = useState({ extendMonth: 6, reason: "" });
  const [stop, setStop] = useState({ reason: "" });
  const [accept, setAccept] = useState({ pass: true, score: 0, note: "" });
  const [email, setEmail] = useState({ subject: "", content: "", toLeader: true, toMembers: true });

  useEffect(() => {
    if (open && topicId) dispatch(getTopicById(topicId));
  }, [open, topicId, dispatch]);

  const t = detail && detail.id === topicId ? detail : null;
  const reload = () => { dispatch(getTopicById(topicId)); };
  const afterAction = async (promise) => {
    const res = await promise;
    if (res?.success) reload();
  };

  // Tab hợp lệ theo trạng thái; nếu tab hiện tại không còn hợp lệ thì về "info".
  const visibleTabs = t ? topicVisibleTabs(t.status) : ["info"];
  useEffect(() => {
    if (t && !topicVisibleTabs(t.status).includes(tab)) setTab("info");
  }, [t, tab]);

  // Hành động chính theo trạng thái + vai trò (thay hàng nút "→ trạng thái" thô).
  const renderPrimaryActions = () => {
    if (!t) return null;
    const A = (label, run, opts = {}) => (
      <Button
        key={label}
        size="small"
        color={opts.color || "primary"}
        variant={opts.variant || "contained"}
        onClick={() => afterAction(run())}
      >
        {label}
      </Button>
    );
    switch (t.status) {
      case "draft":
        return t.proposalFileUrl
          ? A("Nộp đề tài", () => dispatch(submitTopic(topicId, reloadPayload)))
          : <Typography variant="caption" color="error" style={{ marginRight: 8 }}>Cần nộp file thuyết minh (bấm Sửa để bổ sung) trước khi nộp đề tài</Typography>;
      case "submitted":
        return isAdmin ? (
          <>
            {A("Duyệt bắt đầu", () => dispatch(approveTopic(topicId, reloadPayload)))}
            {A("Trả về chỉnh sửa", () => dispatch(returnTopicToDraft(topicId, reloadPayload)), { variant: "outlined", color: "secondary" })}
          </>
        ) : (
          <Typography variant="caption" color="textSecondary" style={{ marginRight: 8 }}>Đang chờ Admin duyệt</Typography>
        );
      case "inProgress":
        return A("Đề nghị nghiệm thu", () => dispatch(requestAcceptance(topicId, reloadPayload)));
      case "suspended":
        return isAdmin
          ? A("Tiếp tục thực hiện", () => dispatch(resumeTopic(topicId, reloadPayload)))
          : <Typography variant="caption" color="textSecondary" style={{ marginRight: 8 }}>Đề tài đang tạm dừng</Typography>;
      case "pendingAcceptance":
        return isAdmin
          ? <Typography variant="caption" color="textSecondary" style={{ marginRight: 8 }}>Nhập kết quả ở tab "Nghiệm thu"</Typography>
          : <Typography variant="caption" color="textSecondary" style={{ marginRight: 8 }}>Chờ hội đồng nghiệm thu</Typography>;
      case "accepted":
      case "failed":
        return isAdmin ? A("Đóng đề tài", () => dispatch(closeTopic(topicId, reloadPayload))) : null;
      default:
        return null;
    }
  };

  const activeStep = t ? topicStepIndex(t.status) : 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Chi tiết đề tài {t?.code ? `- ${t.code}` : ""}{" "}
        {t && <Chip size="small" label={statusLabel(t.status)} style={{ backgroundColor: statusColor(t.status), color: "#fff", marginLeft: 8 }} />}
      </DialogTitle>
      <DialogContent dividers>
        {!t ? (
          <Typography>Đang tải...</Typography>
        ) : (
          <>
            {/* Stepper: vị trí trong vòng đời */}
            <Stepper activeStep={activeStep} alternativeLabel style={{ padding: "8px 0 16px" }}>
              {TOPIC_STEPS.map((step) => (
                <Step key={step.key}>
                  <StepLabel error={(t.status === "failed") && step.key === "pendingAcceptance"}>
                    {step.key === "inProgress" && t.status === "suspended"
                      ? "Tạm dừng"
                      : step.key === "pendingAcceptance" && (t.status === "accepted" || t.status === "failed")
                      ? statusLabel(t.status)
                      : step.label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} indicatorColor="primary" textColor="primary" variant="scrollable">
              {visibleTabs.map((k) => <Tab key={k} value={k} label={TAB_LABELS[k]} />)}
            </Tabs>
            <Box p={2}>
              {tab === "info" && (
                <>
                  <Row label="Tên (VI)">{t.titleVi}</Row>
                  <Row label="Tên (EN)">{t.titleEn}</Row>
                  <Row label="Năm học">{t.year}</Row>
                  <Row label="Cấp">{levelLabel(t.level)}</Row>
                  <Row label="Lĩnh vực">{t.field}</Row>
                  <Row label="Thời lượng">{t.duration} tháng</Row>
                  <Row label="Bắt đầu">{t.startAt ? String(t.startAt).slice(0, 10) : ""}</Row>
                  <Row label="Kết thúc">{t.finishAt ? String(t.finishAt).slice(0, 10) : ""}</Row>
                  <Row label="Chi phí dự kiến">{Number(t.estimatedCost || 0).toLocaleString("vi-VN")} đ</Row>
                  <Row label="Đơn vị chủ trì">{t.hostDepartment?.name}</Row>
                  <Row label="Đơn vị tài trợ">{t.sponsor}</Row>
                  <Row label="Kết quả dự kiến">{t.expectedResult}</Row>
                  <Row label="Người khai báo">{t.createdByName}</Row>
                  <Divider style={{ margin: "12px 0" }} />
                  <Typography variant="subtitle2" gutterBottom>File thuyết minh</Typography>
                  <MultiFileManager
                    fileUrls={t.proposalFileUrl ? [t.proposalFileUrl] : []}
                    setFileUrls={() => {}}
                    newFiles={[]}
                    setNewFiles={() => {}}
                    disabled
                  />
                </>
              )}
              {tab === "members" && (
                (t.members || []).map((m, i) => (
                  <Row key={i} label={roleLabel(m.role)}>
                    {m.fullName || m.profile?.name} {m.email ? `(${m.email})` : ""} {m.faculty ? `- ${m.faculty}` : ""} {m.identityNumber ? `- ${m.identityNumber}` : ""}
                  </Row>
                ))
              )}
              {tab === "progress" && (
                <>
                  {(t.progressReports || []).map((p) => (
                    <Box key={p.id} mb={1}><Typography variant="body2"><b>{String(p.reportDate).slice(0, 10)}</b>: {p.finishedWork} {p.content}</Typography></Box>
                  ))}
                  {!(t.progressReports || []).length && <Typography variant="body2" color="textSecondary">Chưa có báo cáo tiến độ.</Typography>}
                  {(t.status === "inProgress" || t.status === "suspended") && (
                    <>
                      <Divider style={{ margin: "8px 0" }} />
                      <TextField label="Công việc đã hoàn thành" fullWidth multiline rows={2} value={report.finishedWork} onChange={(e) => setReport({ ...report, finishedWork: e.target.value })} />
                      <TextField label="Nội dung" fullWidth multiline rows={2} style={{ marginTop: 8 }} value={report.content} onChange={(e) => setReport({ ...report, content: e.target.value })} />
                      <Button size="small" color="primary" variant="outlined" style={{ marginTop: 8 }}
                        disabled={!report.finishedWork.trim()}
                        onClick={() => afterAction(dispatch(addProgressReport(topicId, report)))}>Thêm báo cáo tiến độ</Button>
                    </>
                  )}
                </>
              )}
              {tab === "extension" && (
                <>
                  {(t.extensions || []).map((e) => (
                    <Box key={e.id} mb={1} display="flex" alignItems="center" style={{ gap: 8 }}>
                      <Typography variant="body2">+{e.extendMonth} tháng — {e.reason}</Typography>
                      <ReqStatusChip status={e.status} />
                      {isAdmin && e.status === "pending" && (
                        <>
                          <Button size="small" color="primary" onClick={() => afterAction(dispatch(approveExtension(e.id, true)))}>Duyệt</Button>
                          <Button size="small" color="secondary" onClick={() => afterAction(dispatch(approveExtension(e.id, false)))}>Từ chối</Button>
                        </>
                      )}
                    </Box>
                  ))}
                  <Divider style={{ margin: "8px 0" }} />
                  <TextField label="Số tháng gia hạn" type="number" value={ext.extendMonth} onChange={(e) => setExt({ ...ext, extendMonth: Number(e.target.value) })} />
                  <TextField label="Lý do" fullWidth multiline rows={2} style={{ marginTop: 8 }} value={ext.reason} onChange={(e) => setExt({ ...ext, reason: e.target.value })} />
                  <Button size="small" color="primary" variant="outlined" style={{ marginTop: 8 }}
                    disabled={!ext.reason.trim()}
                    onClick={() => afterAction(dispatch(requestExtension(topicId, ext)))}>Gửi yêu cầu gia hạn</Button>
                </>
              )}
              {tab === "stop" && (
                <>
                  {(t.stopRequests || []).map((e) => (
                    <Box key={e.id} mb={1} display="flex" alignItems="center" style={{ gap: 8 }}>
                      <Typography variant="body2">{e.reason}</Typography>
                      <ReqStatusChip status={e.status} />
                      {isAdmin && e.status === "pending" && (
                        <>
                          <Button size="small" color="primary" onClick={() => afterAction(dispatch(approveStop(e.id, true, reloadPayload)))}>Duyệt (tạm dừng)</Button>
                          <Button size="small" color="secondary" onClick={() => afterAction(dispatch(approveStop(e.id, false)))}>Từ chối</Button>
                        </>
                      )}
                    </Box>
                  ))}
                  <Divider style={{ margin: "8px 0" }} />
                  <TextField label="Lý do xin dừng" fullWidth multiline rows={2} value={stop.reason} onChange={(e) => setStop({ reason: e.target.value })} />
                  <Button size="small" color="secondary" variant="outlined" style={{ marginTop: 8 }}
                    disabled={!stop.reason.trim()}
                    onClick={() => afterAction(dispatch(requestStop(topicId, stop)))}>Gửi yêu cầu dừng</Button>
                </>
              )}
              {tab === "acceptance" && (
                <>
                  {(t.status === "accepted" || t.status === "failed") && (
                    <>
                      <Row label="Kết quả">{statusLabel(t.status)}</Row>
                      <Row label="Điểm">{t.score}</Row>
                      <Row label="Nhận xét">{t.acceptanceNote}</Row>
                    </>
                  )}
                  {t.status === "closed" && (
                    <>
                      <Row label="Trạng thái">Đã nghiệm thu &amp; đóng</Row>
                      <Row label="Điểm">{t.score}</Row>
                      <Row label="Nhận xét">{t.acceptanceNote}</Row>
                    </>
                  )}
                  {t.status === "pendingAcceptance" && (
                    isAdmin ? (
                      <>
                        <TextField select label="Kết quả" value={accept.pass ? "1" : "0"} onChange={(e) => setAccept({ ...accept, pass: e.target.value === "1" })}>
                          <MenuItem value="1">Đạt</MenuItem>
                          <MenuItem value="0">Không đạt</MenuItem>
                        </TextField>
                        <TextField label="Điểm" type="number" style={{ marginLeft: 8 }} value={accept.score} onChange={(e) => setAccept({ ...accept, score: Number(e.target.value) })} />
                        <TextField label="Nhận xét *" fullWidth multiline rows={2} style={{ marginTop: 8 }} value={accept.note} onChange={(e) => setAccept({ ...accept, note: e.target.value })} />
                        <Button size="small" color="primary" variant="contained" style={{ marginTop: 8 }}
                          disabled={!accept.note.trim()}
                          onClick={() => afterAction(dispatch(setAcceptance(topicId, accept, reloadPayload)))}>Lưu kết quả nghiệm thu</Button>
                      </>
                    ) : (
                      <Typography variant="body2" color="textSecondary">Đề tài đang chờ hội đồng nghiệm thu.</Typography>
                    )
                  )}
                </>
              )}
              {tab === "email" && (
                <>
                  <TextField label="Tiêu đề" fullWidth value={email.subject} onChange={(e) => setEmail({ ...email, subject: e.target.value })} />
                  <TextField label="Nội dung" fullWidth multiline rows={4} style={{ marginTop: 8 }} value={email.content} onChange={(e) => setEmail({ ...email, content: e.target.value })} />
                  <Button size="small" color="primary" variant="outlined" style={{ marginTop: 8 }}
                    onClick={() => dispatch(sendTopicEmail({ topicId, ...email }))}>Gửi cho chủ nhiệm & thành viên</Button>
                </>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {renderPrimaryActions()}
        <Button onClick={onClose}>Đóng</Button>
      </DialogActions>
    </Dialog>
  );
}
